import { AnalyticsStore } from 'bernie-core';
import {
  UitkMessagingCardImageLayout,
  UitkMessagingCardProps,
  UitkMessagingCardSectionProps
} from 'uitk-react-messaging-cards';
import { ActivityDetailBasic, ActivityDestinationViewSearch } from '../../../common/__generated__/api';
import { ActivityMessageBannerProps } from './activity-message-banner';
import Mark = ActivityDestinationViewSearch.Mark;
import Icon = ActivityDestinationViewSearch.Icon;
import ActivityLinkClickAction = ActivityDestinationViewSearch.ActivityLinkClickActionInlineFragment;
import ActivityPopupClickAction = ActivityDestinationViewSearch.ActivityPopupClickActionInlineFragment;

export const ACTIVITY_LINK_CLICK_ACTION = 'ActivityLinkClickAction';
export const ACTIVITY_POPUP_CLICK_ACTION = 'ActivityPopupClickAction';

export function mapLinkAndPopupClickAction(
  clickAction: ActivityDestinationViewSearch._ClickAction | ActivityDetailBasic._ClickAction | undefined
): ActivityLinkClickAction | ActivityPopupClickAction | null {
  if (clickAction?.__typename === ACTIVITY_LINK_CLICK_ACTION) {
    return {
      __typename: clickAction?.__typename,
      linkTagUrl: clickAction.linkTagUrl,
      anchorTagUrl: clickAction.anchorTagUrl,
      opensInNewTab: clickAction.opensInNewTab,
      accessibilityText: (clickAction as ActivityLinkClickAction).accessibilityText,
      analytics: {
        linkName: clickAction.analytics?.linkName || '',
        microMessagePayload: clickAction.analytics?.microMessagePayload || '',
        referrerId: clickAction.analytics?.referrerId || ''
      }
    } as ActivityLinkClickAction;
  } else if (clickAction?.__typename === ACTIVITY_POPUP_CLICK_ACTION) {
    return {
      __typename: clickAction?.__typename,
      text: clickAction.text,
      title: clickAction.title,
      dismissAction: clickAction.dismissAction,
      analytics: {
        linkName: clickAction.analytics?.linkName || '',
        microMessagePayload: clickAction.analytics?.microMessagePayload || '',
        referrerId: clickAction.analytics?.referrerId || ''
      }
    } as ActivityPopupClickAction;
  }

  return null;
}

export function mapMessageTileToBannerProps(
  message: ActivityDestinationViewSearch.ActivityMessageTileInlineFragment
): ActivityMessageBannerProps {
  const first = message?.action?.first;
  const second = message?.action?.second;
  const firstClickAction = mapLinkAndPopupClickAction(first?.clickAction);
  const secondClickAction = mapLinkAndPopupClickAction(second?.clickAction);

  return {
    type: message.type,
    title: message.title,
    subtitle: message.subtitle,
    icon: message.icon,
    mark: message.mark,
    action: {
      first: first &&
        firstClickAction && {
          text: first.text,
          clickAction: firstClickAction
        },
      second: second &&
        secondClickAction && {
          text: second.text,
          clickAction: secondClickAction
        }
    },
    className: ''
  };
}

export function mapPromotionMessageToBannerProps(
  message: ActivityDetailBasic.PromotionMessage
): ActivityMessageBannerProps {
  const primaryAction = message.action?.primary;
  const secondaryAction = message.action?.secondary;
  const firstAction = message.clickAction?.first;
  const secondAction = message.clickAction?.second;
  const firstClickAction = mapLinkAndPopupClickAction(firstAction?.clickAction);
  const secondClickAction = mapLinkAndPopupClickAction(secondAction?.clickAction);

  return {
    type: message.type,
    title: {
      text: message.title
    },
    subtitle: {
      text: message.subtitle
    },
    icon: message.icon,
    mark: message.mark,
    action: {
      first:
        (firstAction &&
          firstClickAction && {
            text: firstAction?.text,
            clickAction: firstClickAction
          }) ||
        (primaryAction && {
          text: primaryAction.text?.split(':')[0] || undefined,
          clickAction: {
            __typename: ACTIVITY_LINK_CLICK_ACTION,
            anchorTagUrl: primaryAction.url,
            opensInNewTab: false,
            accessibilityText: '',
            analytics: {
              referrerId: primaryAction.text?.split(':')[1] || '',
              linkName: ''
            }
          }
        }),
      second:
        (secondAction &&
          secondClickAction && {
            text: secondAction?.text,
            clickAction: secondClickAction
          }) ||
        (secondaryAction && {
          text: secondaryAction.text?.split(':')[0] || undefined,
          clickAction: {
            __typename: ACTIVITY_LINK_CLICK_ACTION,
            anchorTagUrl: secondaryAction.url,
            opensInNewTab: false,
            accessibilityText: '',
            analytics: {
              referrerId: secondaryAction.text?.split(':')[1] || '',
              linkName: ''
            }
          }
        })
    },
    className: ''
  };
}

export const trackAction = (
  rfrr: string | null,
  linkName?: string | null,
  analyticsStore?: AnalyticsStore,
  microMessagePayload?: string[]
): void => {
  if (analyticsStore && typeof analyticsStore.trackEvent === 'function') {
    analyticsStore.trackEvent(
      rfrr,
      linkName || 'RFRR Action Link',
      analyticsStore.omnitureData,
      null,
      true,
      microMessagePayload
    );
  }
};

export function mapMessagingCardSectionProps(
  analyticsStore?: AnalyticsStore,
  title?: string | null,
  first?: ActivityDestinationViewSearch.First | null,
  second?: ActivityDestinationViewSearch.Second | null
): UitkMessagingCardSectionProps {
  let props = {};

  if (title) {
    props = {
      ...props,
      heading: title,
      headingLevel: 'h3'
    };
  }

  if (first && first.clickAction.__typename === ACTIVITY_LINK_CLICK_ACTION) {
    const firstClickAction = mapLinkAndPopupClickAction(first.clickAction) as ActivityLinkClickAction;
    const firstClickAnalytics = first?.clickAction.analytics;
    const firstMicroMessagePayload = firstClickAnalytics?.microMessagePayload
      ? [firstClickAnalytics?.microMessagePayload]
      : [];
    props = {
      ...props,
      linkOneHref: firstClickAction.anchorTagUrl || firstClickAction.linkTagUrl,
      linkOneText: first.text
    };
    if (firstClickAnalytics?.referrerId) {
      props = {
        ...props,
        linkOneAttributes: {
          onClick: (): void =>
            trackAction(
              firstClickAnalytics?.referrerId as string,
              firstClickAnalytics?.linkName,
              analyticsStore,
              firstMicroMessagePayload
            )
        }
      };
    }
  }

  if (second && second.clickAction.__typename === ACTIVITY_LINK_CLICK_ACTION) {
    const secondClickAction = mapLinkAndPopupClickAction(second?.clickAction) as ActivityLinkClickAction;
    const secondClickAnalytics = second?.clickAction?.analytics;
    const secondMicroMessagePayload = secondClickAnalytics?.microMessagePayload
      ? [secondClickAnalytics?.microMessagePayload]
      : [];
    props = {
      ...props,
      linkTwoHref: secondClickAction.anchorTagUrl || secondClickAction.linkTagUrl,
      linkTwoText: second.text
    };
    if (secondClickAnalytics?.referrerId) {
      props = {
        ...props,
        linkTwoAttributes: {
          onClick: (): void =>
            trackAction(
              secondClickAnalytics?.referrerId as string,
              secondClickAnalytics?.linkName,
              analyticsStore,
              secondMicroMessagePayload
            )
        }
      };
    }
  }

  return props;
}

export function mapMessagingCardMediaProps(
  icon?: Icon | null,
  mark?: Mark | null,
  styleClass?: string | null
): UitkMessagingCardProps {
  let props: UitkMessagingCardProps = {};

  if (icon || mark) {
    props = {
      ...props,
      imageLayout: UitkMessagingCardImageLayout.LEFT
    };
  }

  if (styleClass) {
    props = {
      ...props,
      className: styleClass
    };
  }

  return props;
}
